import React, { useEffect, useMemo, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmt, fmtDate, today } from "@/lib/format";

const INCOME_CATEGORIES = ["Poultry Sales", "Farm Sales", "Other Income"];
const EXPENSE_CATEGORIES = ["Feed", "Medicine", "Building Materials", "Transport", "Fuel", "Water", "Electricity", "Labour", "Health", "Household", "Miscellaneous"];

export default function Finance() {
  const [txs, setTxs] = useState([]);
  const [dash, setDash] = useState(null);
  async function refresh() {
    try {
      const [t, d] = await Promise.all([api.get("/finance/transactions"), api.get("/finance/dashboard")]);
      setTxs(t.data); setDash(d.data);
    } catch { toast.error("Could not load Finance360"); }
  }
  useEffect(() => { refresh(); }, []);

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Module</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight">Finance360</h1>
        <p className="subtle mt-1">Homestead income, expenses and cash position.</p>
      </div>

      {dash && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <KPI label="Cash Position" value={fmt(dash.cash_position)} icon={Wallet} testid="fin-cash" />
          <KPI label="Income" value={fmt(dash.total_income)} icon={TrendingUp} testid="fin-income" />
          <KPI label="Expenses" value={fmt(dash.total_expense)} icon={TrendingDown} testid="fin-expense" />
          <KPI label="Profit" value={fmt(dash.profit)} icon={Wallet} testid="fin-profit" />
        </div>
      )}

      {dash && dash.monthly_trend?.length > 0 && (
        <Card className="rounded-2xl">
          <CardHeader><CardTitle className="text-base">Monthly income vs. expenses</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dash.monthly_trend}>
                <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="income" fill="#0A3F2C" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expense" fill="#D4AF37" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div><CardTitle>Transactions</CardTitle><CardDescription>{txs.length} recorded</CardDescription></div>
          <NewTx onCreated={refresh} />
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Category</TableHead>
              <TableHead>Description</TableHead><TableHead className="text-right">Amount</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {txs.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>{fmtDate(t.date)}</TableCell>
                  <TableCell><Badge variant={t.type === "income" ? "default" : "outline"} className={t.type === "income" ? "bg-deepgreen text-deepgreen-foreground" : ""}>{t.type}</Badge></TableCell>
                  <TableCell>{t.category}</TableCell>
                  <TableCell className="max-w-md truncate">{t.description}</TableCell>
                  <TableCell className={`text-right font-medium ${t.type === "income" ? "text-deepgreen" : "text-destructive"}`}>{fmt(t.amount)}</TableCell>
                </TableRow>
              ))}
              {txs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center subtle py-8">No transactions yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function KPI({ label, value, icon: Icon, testid }) {
  return (
    <div className="kpi-card" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="mt-2 font-heading text-2xl font-semibold tracking-tight">{value}</div>
        </div>
        <div className="h-10 w-10 rounded-xl bg-deepgreen/10 text-deepgreen flex items-center justify-center"><Icon className="h-5 w-5" /></div>
      </div>
    </div>
  );
}

function NewTx({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: today(), type: "expense", category: "Feed", amount: 0, description: "" });
  const cats = form.type === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
  async function submit() {
    try { await api.post("/finance/transactions", { ...form, amount: Number(form.amount) }); toast.success("Transaction added"); setOpen(false); onCreated(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-deepgreen hover:bg-deepgreen/90" data-testid="new-tx-btn"><Plus className="h-4 w-4 mr-1" />New transaction</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New transaction</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          <div>
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, category: v === "income" ? INCOME_CATEGORIES[0] : EXPENSE_CATEGORIES[0] })}>
              <SelectTrigger data-testid="tx-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="income">Income</SelectItem>
                <SelectItem value="expense">Expense</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Amount</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="tx-amount-input" /></div>
          <div className="col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="tx-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
